import React, { memo, useMemo } from 'react';

import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import uniqBy from 'lodash/uniqBy';

import { CopyIcon } from 'icons';
import { useAutoDisposableTooltipState, useScreenWidth } from 'hooks';
import {
  countriesLinks,
  baseHiringGuidesURL,
  costCalculatorURL,
  costCalculatorForCountryURL,
} from './consts';

import type { TimeZoneWithCountryData } from 'types';
import { copyTextToClipboard } from 'iframe';
import { ResultsTable } from './DesktopResultsTable';
import { MobileResultsList } from './MobileResultsList';
import { countriesWithCodesAndStates, humanDevelopmentIndex } from './consts';
import { AugmentedTimeZoneWithCountryData } from './types';
import {
  FilteringWithSortPanel,
  useFilteringWithSortState,
} from './FilteringWithSortPanel';

import '/node_modules/flag-icons/css/flag-icons.min.css';

interface TimeZonesInfoListProps {
  timeZonesWithCountries: TimeZoneWithCountryData[];
}

export const TimeZonesInfoList = memo(
  ({ timeZonesWithCountries }: TimeZonesInfoListProps) => {
    const { isMobile } = useScreenWidth();
    const { triggerTooltip, tooltipText } = useAutoDisposableTooltipState(
      'Copy countries list'
    );

    const augmentedWithExtraData = useMemo<
      AugmentedTimeZoneWithCountryData[]
    >(() => {
      return timeZonesWithCountries.map((el) => {
        const HDIData = humanDevelopmentIndex.find(
          (record) => record.countryCode === el.country.id
        );

        const costCalcData = countriesWithCodesAndStates.find(
          (record) => record.countryCode === el.country.id
        );

        return {
          ...el,
          hiringGuidesData: {
            url: countriesLinks[el.country.name]
              ? `${baseHiringGuidesURL}${countriesLinks[el.country.name]}`
              : null,
          },
          costCalcData: {
            url: costCalcData
              ? `${costCalculatorForCountryURL}${costCalcData.urlSlug}`
              : costCalculatorURL,
          },
          HDIData: {
            rating: HDIData?.rating,
            index: HDIData?.index,
          },
        };
      });
    }, [timeZonesWithCountries]);

    const {
      onlyShowRecordsWithLowHDI,
      searchValues,
      onlyShowRecordsWithHiringData,
      searchCountriesOptions,
      sortField,
      sortDirection,
      setOnlyShowRecordsWithLowHDI,
      setOnlyShowRecordsWithHiringData,
      setSorting,
      setSearchValues,
      sortedData,
    } = useFilteringWithSortState(augmentedWithExtraData);

    const renderResults = () => {
      return (
        <>
          {isMobile ? (
            <MobileResultsList data={sortedData} />
          ) : (
            <ResultsTable data={sortedData} />
          )}
        </>
      );
    };

    return (
      <>
        <Typography
          variant="h2"
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            mb: 8,
          }}
        >
          <Typography sx={{ mr: 'auto' }} variant="h2">
            List of all time zones
          </Typography>
          <Tooltip title={tooltipText} placement="top">
            <Button
              size="small"
              variant="outlined"
              disabled={!sortedData}
              onClick={() => {
                if (!sortedData) return;

                const uniqueCountriesList = uniqBy(
                  sortedData,
                  'country.id'
                ).map(({ country }) => country.name);

                copyTextToClipboard(
                  uniqueCountriesList.join(',\\r\\n') +
                    '\\n\\nCreated using:\\nhttps://www.oysterhr.com/global-employment-tools/timezone-crossover-calculator'
                );
                triggerTooltip('Copied!');
              }}
            >
              <CopyIcon width={16} height={16} style={{ marginRight: '6px' }} />
              Copy countries
            </Button>
          </Tooltip>
        </Typography>

        <FilteringWithSortPanel
          onlyShowRecordsWithLowHDI={onlyShowRecordsWithLowHDI}
          searchValues={searchValues}
          onlyShowRecordsWithHiringData={onlyShowRecordsWithHiringData}
          searchCountriesOptions={searchCountriesOptions}
          sortField={sortField}
          sortDirection={sortDirection}
          setOnlyShowRecordsWithLowHDI={setOnlyShowRecordsWithLowHDI}
          setOnlyShowRecordsWithHiringData={setOnlyShowRecordsWithHiringData}
          setSorting={setSorting}
          setSearchValues={setSearchValues}
        />

        {!!timeZonesWithCountries.length && renderResults()}
      </>
    );
  }
);

TimeZonesInfoList.displayName = 'TimeZonesInfoList';
